import { useState } from 'react';
import { useToast } from '../../components/Toast.js';
import { Dialog, Field, Notice, TextAreaField, formatDateTime } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { useMeetings, type MeetingRow } from './data.js';
import { MeetingForm } from './MeetingForm.js';

/**
 * The meetings of a contact or a lead, on its sheet (decision of Nov 3,
 * 2026), with the Meeting button: the whole team sees every meeting.
 */
export function MeetingsBlock({
  contact,
  lead,
  people,
}: {
  /** The contact sheet's contact. */
  contact?: { id: string; label: string; company: string | null; country?: string | null };
  /** The lead sheet's lead. */
  lead?: { id: string; label: string };
  /** The lead's contacts, met with it by default. */
  people?: { id: string; label: string; company: string | null }[];
}) {
  const { can, user } = useSignedIn();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MeetingRow | null>(null);
  const [saved, setSaved] = useState(0);
  const rows = useMeetings(contact ? { contactId: contact.id } : { leadId: lead?.id }, saved);
  if (!can('meetings.read.all') && !can('meetings.create')) return null;
  return (
    <div className="formsec lk-sec" role="group" aria-label="Meetings">
      <h3>Meetings</h3>
      {rows && rows.length === 0 && (
        <p className="note" style={{ margin: 0 }}>
          No meeting yet.
        </p>
      )}
      {rows?.map((m) => (
        <div key={m.id} className="lk-card">
          <span className="lk-top">
            <b>{m.title || m.contacts.map((c) => c.name).join(', ') || 'Meeting'}</b>
            <span className="badge b-ok">{formatDateTime(m.met_at)}</span>
          </span>
          {m.title && <span className="note">{m.contacts.map((c) => c.name).join(', ')}</span>}
          {m.notes && <span className="note">{m.notes}</span>}
          {m.next_step && (
            <span className="note">
              Next step: {m.next_step}
              {m.next_step_due ? `, ${m.next_step_due}` : ''}
            </span>
          )}
          <span className="lk-meta">
            <span>{m.attendees.join(', ')}</span>
            <span>
              {m.waiting
                ? 'On this device, not sent yet'
                : m.hubspot_status === 'synced'
                  ? 'In HubSpot'
                  : m.hubspot_status === 'error'
                    ? 'HubSpot refused it: Settings, HubSpot'
                    : 'Going to HubSpot'}
              {m.event_code ? `, ${m.event_code}` : ''}
            </span>
          </span>
          {(can('meetings.update.all') ||
            (can('meetings.update.own') && m.created_by === user.id)) && (
            <div className="actions">
              <button
                type="button"
                className="btn sm"
                aria-label={`Edit the meeting of ${formatDateTime(m.met_at)}`}
                onClick={() => setEditing(m)}
              >
                Edit
              </button>
            </div>
          )}
        </div>
      ))}
      {can('meetings.create') && (
        <div className="actions">
          <button type="button" className="btn sm primary" onClick={() => setOpen(true)}>
            Meeting
          </button>
        </div>
      )}
      {editing && (
        <EditMeeting
          meeting={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setSaved((n) => n + 1);
          }}
        />
      )}
      {open && (
        <MeetingForm
          people={contact ? [contact] : (people ?? [])}
          lead={lead ?? null}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            setSaved((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

/**
 * A meeting already saved, its title and notes changed (Nov 4, 2026): on the
 * device first, then the same meeting updated in HubSpot, never a new one.
 */
function EditMeeting({
  meeting,
  onClose,
  onSaved,
}: {
  meeting: MeetingRow;
  onClose(): void;
  onSaved(): void;
}) {
  const { save } = useOffline();
  const toast = useToast();
  const [title, setTitle] = useState(meeting.title ?? '');
  const [notes, setNotes] = useState(meeting.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit() {
    if (saving) return;
    setSaving(true);
    setProblem(null);
    try {
      await save({
        opId: newId(),
        entity: 'meeting',
        id: meeting.id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields: { title: title.trim() || null, notes: notes.trim() || null },
      });
      toast(
        meeting.hubspot_status === 'synced'
          ? 'Meeting changed. HubSpot gets the change by itself.'
          : 'Meeting changed.',
      );
      onSaved();
    } catch {
      setProblem('This device could not save the change. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Edit the meeting"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => void submit()}
          >
            Save
          </button>
        </>
      }
    >
      <div className="dlg-form">
        <Field
          label="Title (optional)"
          hint="The meeting's title in HubSpot"
          value={title}
          maxLength={250}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextAreaField
          label="Notes"
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {problem && <Notice kind="error">{problem}</Notice>}
      </div>
    </Dialog>
  );
}
